// Client-side PQRSD staff/citizen notifications.
//
// These fire from the browser after an operator action in PqrsdService (assign /
// forward / return / reassign / take-in-progress) commits its Firestore write.
//
// The actual email is NOT sent from here. The browser cannot reach the
// `send-email` Cloud Function directly (it's only exposed to internal
// service-to-service traffic), so instead we POST to `handlePqrsd`
// (action: "notify") with the caller's Firebase ID token. That function renders
// the shared template server-side and calls `send-email` service-to-service —
// the same path every other email in the app uses. It also re-derives the
// recipients and PQRSD fields from the freshly-committed filed doc, so we only
// pass the event type, the radicado, and event-specific text (fromName/reason).
//
// Every send is best-effort: a transient failure never blocks the Firestore
// write the caller already committed, so all errors are swallowed.

import { server } from "../../config/ConfigFirebase";
import { getToken } from "../auth/AuthServices";

// The notification dispatcher lives on handlePqrsd. `server` is
// `{region}-{projectId}` (e.g. "us-east1-royalplusdev"), matching
// PqrsdService.pqrsdFunctionUrl — resolves to each environment's own project.
const notifyUrl = () => `https://${server}.cloudfunctions.net/handlePqrsd`;

// Core caller: attaches a fresh Firebase ID token and POSTs the notify request.
// Returns a small result object; never throws.
const postNotify = async (payload) => {
  try {
    const token = await getToken();
    if (!token) return { sent: false, reason: "no auth token" };
    const resp = await fetch(notifyUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ action: "notify", ...payload }),
    });
    return { sent: resp.ok, status: resp.status };
  } catch (e) {
    console.warn("[PqrsdEmail] notify failed (non-fatal):", e?.message);
    return { sent: false, reason: e?.message };
  }
};

// --- Staff notifications -----------------------------------------------------

// One PQRSD assigned to one or more operators. The server reads the assignees
// (with their emails) off the freshly-committed filed doc and emails each one.
export const notifyStaffAssigned = async ({ radicado } = {}) =>
  postNotify({ event: "assigned", radicado });

// PQRSD handed off to another operator (the new assignee on the filed doc).
export const notifyStaffForwarded = async ({ radicado, fromName } = {}) =>
  postNotify({ event: "forwarded", radicado, fromName });

// PQRSD declined by its operator and sent back to the admin queue. The server
// routes it to the PQRSD owner mailbox so a supervisor reassigns it.
export const notifyStaffReturned = async ({ radicado, fromName, reason } = {}) =>
  postNotify({ event: "returned", radicado, fromName, reason });

// A returned PQRSD reassigned to one or more new operators.
export const notifyStaffReassigned = async ({ radicado } = {}) =>
  postNotify({ event: "reassigned", radicado });

// --- Citizen notifications ---------------------------------------------------

// Citizen told an operator is now working on their PQRSD. Server-side send lets
// this carry a real one-click magic link (the old browser path could only link
// to the tokenless tracking page).
export const notifyCitizenInProgress = async ({ radicado } = {}) =>
  postNotify({ event: "in_progress", radicado });
